import { GeckoMeasurementRange } from 'types/measurement/geckoMeasurementRange'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoComponentCoords } from './geckoComponentCoords'
import { GeckoComponentSpace } from './geckoComponentSpace'

/**
 * A method for checking if a rect contains a given point, including wrapping.
 * Assumes that rect and coord are in the same units.
 * @param rectXStart
 * @param rectYStart
 * @param rectXEnd
 * @param rectYEnd
 * @param coordX
 * @param coordY
 * @returns
 */
export function contains(
  rectXStart: number,
  rectYStart: number,
  rectXEnd: number,
  rectYEnd: number,
  coordX: number,
  coordY: number
): boolean {
  return (
    (coordX <= Math.max(rectXStart, rectXEnd) &&
      coordX >= Math.min(rectXStart, rectXEnd)) ===
      rectXStart <= rectXEnd &&
    (coordY <= Math.max(rectYStart, rectYEnd) &&
      coordY >= Math.min(rectYStart, rectYEnd)) ===
      rectYStart <= rectYEnd
  )
}

/**
 * A class representing the space bounded by two coordinates on the surface of
 * a component. Note that for some component types, (such as cones) a rect might
 * trace out a non-rectangular shape. Also note that coordinates can wrap on
 * some components, so the "start" point may appear to the right of the "end"
 * point in 3D space.
 */
export class GeckoComponentRect {
  //----------------------------------------------------------------------------

  /** The start point of this rect, generally its bottom-left */
  public readonly start: GeckoComponentCoords

  /** The start point of this rect, generally its top-right */
  public readonly end: GeckoComponentCoords

  /** The range of x values over this rect */
  public readonly x: GeckoMeasurementRange

  /** The range of Y values over this rect */
  public readonly y: GeckoMeasurementRange

  /**
   * A simple constructor- private to encourage use of more descriptive static
   * factory methods like CreateFromCoords()
   * @param start         The start point of this rect
   * @param end           The end point of this rect
   * @private
   */
  private constructor(start: GeckoComponentCoords, end: GeckoComponentCoords) {
    console.assert(start.componentID === end.componentID)
    console.assert(start.x.unit === end.x.unit && start.y.unit === end.y.unit)
    this.start = start
    this.end = end
    this.x = GeckoMeasurementRange.CreateFromStartEnd(this.start.x, this.end.x)
    this.y = GeckoMeasurementRange.CreateFromStartEnd(this.start.y, this.end.y)
  }

  /**
   * An accessor method for this object's component ID (an accessor rather than
   * a variable since this is already stored on both the start and end
   * coordinates)
   */
  public get componentID() {
    return this.start.componentID
  }

  /**
   * An accessor method for units of the 2 dimensional space that this rect
   * exists in.
   */
  public get space() {
    return this.start.space
  }

  /** The width of this rect, always positive */
  public get width() {
    return this.x.size
  }

  /** The height of this rect, always positive */
  public get height() {
    return this.y.size
  }

  /**
   * Factory method for creating a GeckoComponentRect with a component ID, start
   * and end coordinates, and associated measurement units for those
   * coordinates.
   * @param componentID   The ID of the component this rect is on
   * @param xStart        The x coordinate of the start point of this rect
   * @param yStart        The y coordinate of the start point of this rect
   * @param xEnd          The x coordinate of the end point of this rect
   * @param yEnd          The y coordinate of the endEnd   point of this rect
   * @param xUnit         The measurement unit of this rect's x coordinates
   * @param yUnit         The measurement unit of this rect's y coordinates
   * @constructor
   */
  public static CreateFromXYs(
    componentID: string,
    xStart: number,
    yStart: number,
    xEnd: number,
    yEnd: number,
    xUnit: GeckoMeasurementUnit,
    yUnit: GeckoMeasurementUnit
  ) {
    return new GeckoComponentRect(
      GeckoComponentCoords.CreateFromValues(
        componentID,
        xStart,
        xUnit,
        yStart,
        yUnit
      ),
      GeckoComponentCoords.CreateFromValues(
        componentID,
        xEnd,
        xUnit,
        yEnd,
        yUnit
      )
    )
  }

  /**
   * Factory method for creating a GeckoComponentRect from a componentID and
   * JSON structured as { x_start, x_end, y_start, y_end }. Returns undefined
   * if the JSON is structured incorrectly.
   * @param componentID   The ID of the component this rect is on
   * @param json          JSON describing the boundaries of this rect
   * @constructor
   */
  public static CreateFromJSON(
    componentID: string,
    json: Record<string, unknown> | undefined
  ): GeckoComponentRect | undefined {
    if (!json) return undefined
    const xStart = parseNumber(json.x_start)
    const yStart = parseNumber(json.y_start)
    if (xStart === undefined || yStart === undefined) return undefined

    const xEnd = parseNumber(json.x_end)
    const yEnd = parseNumber(json.y_end)
    if (xEnd === undefined || yEnd === undefined) return undefined

    const xUnit = GeckoMeasurementUnit.GetByID(
      parseString(json.x_measurement_unit)
    )
    const yUnit = GeckoMeasurementUnit.GetByID(
      parseString(json.y_measurement_unit)
    )
    if (xUnit === undefined || yUnit === undefined) return undefined

    return GeckoComponentRect.CreateFromXYs(
      componentID,
      xStart,
      yStart,
      xEnd,
      yEnd,
      xUnit,
      yUnit
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A factory method creating a GeckoComponentCoords object from
   * two GeckoComponentCoords
   * @param start         The start point of this rect
   * @param end           The end point of this rect
   * @constructor
   */
  public static CreateFromCoords(
    start: GeckoComponentCoords,
    end: GeckoComponentCoords
  ) {
    return new GeckoComponentRect(start, end)
  }

  /**
   * A factory method creating a GeckoComponentCoords object from
   * two GeckoMeasurementRanges
   * @param componentID The ID of the component this rect is on
   * @param xRange The x range of the rect
   * @param yRange The y range of the rect
   * @constructor
   */
  public static CreateFromRanges(
    componentID: string,
    xRange: GeckoMeasurementRange,
    yRange: GeckoMeasurementRange
  ) {
    const start = GeckoComponentCoords.CreateFromMeasurements(
      componentID,
      xRange.start,
      yRange.start
    )
    const end = GeckoComponentCoords.CreateFromMeasurements(
      componentID,
      xRange.end,
      yRange.end
    )
    return new GeckoComponentRect(start, end)
  }

  /**
   * A method for checking whether two component rects are exactly equal,
   * not just equivalent. (0 in, 0 in) - (12 in, 12 in) is not "Equal" to
   * (0 ft, 0 ft) - (1 ft, 1 ft)
   * @param a               The first GeckoComponentRect object, or undefined
   * @param b               The second GeckoComponentRect object, or undefined
   * @constructor
   */
  public static Equals(
    a: GeckoComponentRect | undefined,
    b: GeckoComponentRect | undefined
  ): boolean {
    if (a === b) return true
    if (!a) return !b
    if (!b) return !a
    return (
      GeckoComponentCoords.Equals(a.start, b.start) &&
      GeckoComponentCoords.Equals(a.end, b.end)
    )
  }

  /**
   * A method for checking whether two component rects overlap, including
   * wrapping.
   * @param a               The first GeckoComponentRect object, or undefined
   * @param b               The second GeckoComponentRect object, or undefined
   * @constructor
   */
  public static Overlaps(
    a: GeckoComponentRect | undefined,
    b: GeckoComponentRect | undefined
  ): boolean {
    if (!a || !b) return false
    if (a.componentID !== b.componentID) return false

    const aWrapsX = a.start.x.num > a.end.x.num
    const aWrapsY = a.start.y.num > a.end.y.num
    const second = b.project(a.space)
    const bWrapsX = second.start.x.num > second.end.x.num
    const bWrapsY = second.start.y.num > second.end.y.num

    const axe = a.x.end.num
    const axs = a.x.start.num
    const bxe = second.x.end.num
    const bxs = second.x.start.num

    const aye = a.y.end.num
    const ays = a.y.start.num
    const bye = second.y.end.num
    const bys = second.y.start.num

    const xOverlaps =
      (axs <= bxe && axe >= bxs) || // non-wrapping case
      (axs <= (bWrapsX ? Infinity : bxe) &&
        (aWrapsX ? Infinity : axe) >= bxs) || // check for overlap on left side
      ((aWrapsX ? -Infinity : axs) <= bxe && axe >= (bWrapsX ? -Infinity : bxs)) // check for overlap on right side

    const yOverlaps =
      (ays <= bye && aye >= bys) || // non-wrapping case
      (ays <= (bWrapsY ? Infinity : bye) &&
        (aWrapsY ? Infinity : aye) >= bys) || // check for overlap on top side
      ((aWrapsY ? -Infinity : ays) <= bye && aye >= (bWrapsY ? -Infinity : bys)) // check for overlap on bottom side

    return xOverlaps && yOverlaps
  }

  /**
   * A method for checking if a rect contains a given point, including wrapping.
   * @param rect            The rect to use for bounds-checking
   * @param coords          The coordinates to check
   * @constructor
   */
  public static Contains(
    rect: GeckoComponentRect | undefined,
    coords: GeckoComponentCoords | undefined
  ): boolean {
    if (!rect || !coords) return false
    if (rect.componentID !== coords.componentID) return false
    const projectedCoord = coords.project(rect.space)
    return contains(
      rect.x.start.num,
      rect.y.start.num,
      rect.x.end.num,
      rect.y.end.num,
      projectedCoord.x.num,
      projectedCoord.y.num
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A method which returns a GeckoComponentRect object with new measurement
   * units. Note that if no conversion is necessary, this instance will be
   * returned.
   * @param xUnit           The new measurement unit of the x coordinate
   * @param yUnit           The new measurement unit of the y coordinate
   */
  public convertTo(
    xUnit: GeckoMeasurementUnit | undefined,
    yUnit: GeckoMeasurementUnit | undefined
  ): GeckoComponentRect {
    if ((this.x.unit === xUnit || !xUnit) && (this.y.unit === yUnit || !yUnit))
      return this
    if (this.x.unit === xUnit && this.y.unit === yUnit) return this
    return GeckoComponentRect.CreateFromCoords(
      this.start.convertTo(xUnit, yUnit),
      this.end.convertTo(xUnit, yUnit)
    )
  }

  /**
   * A method which returns a GeckoComponentRect object with new measurement
   * units, so long as the component IDs match.
   * @param space           The new 2-dimensional measurement space to convert to
   */
  public project(space: GeckoComponentSpace): GeckoComponentRect {
    if (this.componentID !== space.componentID)
      throw new Error(
        `trying to convert a ComponentRect in a space on ${this.componentID} 
        to a space on ${space.componentID}`
      )
    if (this.x.unit === space.x.unit && this.y.unit === space.y.unit)
      return this
    return GeckoComponentRect.CreateFromCoords(
      this.start.project(space),
      this.end.project(space)
    )
  }

  /**
   * A method returning a legible string form of this object's data
   */
  public toString(): string {
    return `${this.componentID} (${this.start.x}, ${this.start.y}) - 
    (${this.end.x}, ${this.end.y})`
  }

  public toJSON(): Record<string, unknown> {
    return {
      x_start: this.start.x.num,
      y_start: this.start.y.num,
      x_end: this.end.x.num,
      y_end: this.end.y.num,
      x_measurement_unit: this.x.unit.id,
      y_measurement_unit: this.y.unit.id,
    }
  }
}
